import React from 'react';

/**
 * CountryFlag — renders a country flag as an inline SVG.
 *
 * Why not emoji? Older iOS browsers, Windows Chrome and a chunk of corporate
 * desktops do NOT render regional-indicator emoji pairs (🇬🇧 → □□). SVG is
 * deterministic across all platforms and screenshots cleanly in marketing
 * materials. Lightweight (~600 bytes per flag).
 *
 * Public API:
 *     <CountryFlag code="UK" className="w-5 h-4" title="United Kingdom" />
 *
 * Supported codes: UK · IN · CA · US · AU · GLOBE (fallback)
 */
const FLAGS = {
  UK: (
    <svg viewBox="0 0 60 30" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <clipPath id="t"><path d="M30,15 h30 v15 z v15 h-30 z h-30 v-15 z v-15 h30 z" /></clipPath>
      <rect width="60" height="30" fill="#012169" />
      <path d="M0,0 L60,30 M60,0 L0,30" stroke="#fff" strokeWidth="6" />
      <path d="M0,0 L60,30 M60,0 L0,30" clipPath="url(#t)" stroke="#C8102E" strokeWidth="4" />
      <path d="M30,0 v30 M0,15 h60" stroke="#fff" strokeWidth="10" />
      <path d="M30,0 v30 M0,15 h60" stroke="#C8102E" strokeWidth="6" />
    </svg>
  ),
  IN: (
    <svg viewBox="0 0 60 40" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <rect width="60" height="13.33" y="0" fill="#FF9933" />
      <rect width="60" height="13.34" y="13.33" fill="#FFFFFF" />
      <rect width="60" height="13.33" y="26.67" fill="#138808" />
      <circle cx="30" cy="20" r="4" fill="none" stroke="#000080" strokeWidth="0.6" />
      <circle cx="30" cy="20" r="0.8" fill="#000080" />
      {[...Array(24)].map((_, i) => (
        <line key={i} x1="30" y1="20" x2={30 + 4 * Math.cos((i * 15 * Math.PI) / 180)} y2={20 + 4 * Math.sin((i * 15 * Math.PI) / 180)} stroke="#000080" strokeWidth="0.3" />
      ))}
    </svg>
  ),
  CA: (
    <svg viewBox="0 0 60 30" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <rect width="15" height="30" x="0" fill="#D52B1E" />
      <rect width="30" height="30" x="15" fill="#FFFFFF" />
      <rect width="15" height="30" x="45" fill="#D52B1E" />
      <path d="M30,8 l1.5,3 3,-0.5 -2,2.5 2,2.5 -3,-0.5 -1.5,3 -1.5,-3 -3,0.5 2,-2.5 -2,-2.5 3,0.5 z" fill="#D52B1E" />
    </svg>
  ),
  US: (
    <svg viewBox="0 0 60 30" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <rect width="60" height="30" fill="#fff" />
      {[0, 2, 4, 6, 8, 10, 12].map((i) => (
        <rect key={i} y={i * 2.3} width="60" height="2.3" fill="#B22234" />
      ))}
      <rect width="24" height="16.1" fill="#3C3B6E" />
    </svg>
  ),
  AU: (
    <svg viewBox="0 0 60 30" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <rect width="60" height="30" fill="#012169" />
      <path d="M0,0 L30,15 M30,0 L0,15" stroke="#fff" strokeWidth="2.5" />
      <path d="M30,0 v15 M0,7.5 h30" stroke="#fff" strokeWidth="4" />
      <path d="M30,0 v15 M0,7.5 h30" stroke="#C8102E" strokeWidth="2.5" />
    </svg>
  ),
  GLOBE: (
    <svg viewBox="0 0 30 30" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <circle cx="15" cy="15" r="13" fill="#1E40AF" stroke="#fff" strokeWidth="1" />
      <ellipse cx="15" cy="15" rx="13" ry="5" fill="none" stroke="#fff" strokeWidth="0.6" />
      <line x1="2" y1="15" x2="28" y2="15" stroke="#fff" strokeWidth="0.6" />
      <line x1="15" y1="2" x2="15" y2="28" stroke="#fff" strokeWidth="0.6" />
    </svg>
  ),
};

const CountryFlag = ({ code = 'GLOBE', className = 'w-5 h-4 inline-block align-middle rounded-sm', title = '' }) => {
  const cc = (code || '').toUpperCase();
  const flag = FLAGS[cc] || FLAGS.GLOBE;
  return (
    <span className={className} title={title || cc} role="img" aria-label={title || cc} data-testid={`country-flag-${cc}`}>
      {flag}
    </span>
  );
};

export default CountryFlag;
